import { useSelector } from '@xstate/react';
import React, { useEffect, useRef } from 'react';

import {
  HStack,
  NewButton,
  P,
  Textarea,
  VStack,
  legacyTextStyles,
} from '@eluve/components';
import { getReadableTime } from '@eluve/date-utils';

import { MemoizedUtteranceSplitter } from './MemoizedUtteranceSplitter';
import { useSegmentAudioContext } from './SegmentAudioContextStore';
import { MemoizedTranscriptWord } from './TranscriptWord';
import { Utterance } from './types';

interface RegularTranscriptProps {
  isReadonly: boolean;
  utterance: Utterance;
  nextUtterance?: Utterance | null;
  showConfidenceStyles: boolean;
  splitUtterance: (params: { utteranceId: string; fragmentId: string }) => void;
  shouldDisplaySplitter: (
    nextStartAt: number | null | undefined,
    endAt: number | null | undefined,
  ) => boolean;
}

interface FocusableTranscriptProps extends RegularTranscriptProps {
  onUtteranceChanged: (id: string, text: string) => void;
}

export const FocusableTranscript: React.FC<FocusableTranscriptProps> = ({
  isReadonly,
  utterance,
  nextUtterance,
  showConfidenceStyles,
  onUtteranceChanged,
  splitUtterance,
  shouldDisplaySplitter,
}) => {
  const { store } = useSegmentAudioContext();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isFocused = useSelector(
    store,
    (state) => state.context.focusedUtteranceId === utterance.id,
  );

  useEffect(() => {
    const newUtterance = inputRef.current?.value.trim() ?? '';
    if (newUtterance !== utterance.text && newUtterance.length > 0) {
      onUtteranceChanged(utterance.id, newUtterance);
    }
  }, [isFocused, onUtteranceChanged, utterance.id, utterance.text]);

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const newUtterance = e.target.value.trim();
    if (newUtterance !== utterance.text) {
      onUtteranceChanged(utterance.id, newUtterance);
    }
  };

  // Shared handler for word clicks to maintain focus
  const handleWordClick = () => {
    // Refocus the input element after word navigation is triggered
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (isReadonly) {
    return (
      <UnfocusedUtteranceLine
        utterance={utterance}
        nextUtterance={nextUtterance}
        showConfidenceStyles={showConfidenceStyles}
        splitUtterance={splitUtterance}
        shouldDisplaySplitter={shouldDisplaySplitter}
        isReadonly={isReadonly}
      />
    );
  }

  if (isFocused) {
    return (
      <VStack className="grid grid-cols-[auto_1fr_auto] items-center gap-2 pl-2 pr-4">
        <span>Original:</span>
        <FocusedUtteranceLine
          utterance={utterance}
          nextUtterance={nextUtterance}
          showConfidenceStyles={showConfidenceStyles}
          onWordClick={handleWordClick}
          splitUtterance={splitUtterance}
          shouldDisplaySplitter={shouldDisplaySplitter}
        />
        <HStack className="row-span-2 size-full items-end">
          <NewButton
            size="m"
            onClick={() => {
              store.send({ type: 'clearFocusedUtteranceId' });
              inputRef.current?.blur();
            }}
            text="Close"
            aria-label="Close"
          />
        </HStack>
        <span>Edit:</span>
        <Textarea
          ref={inputRef}
          className="px-2 py-1 text-base font-medium text-text-primary"
          autoFocus
          defaultValue={utterance.text ?? ''}
          onBlur={handleBlur}
        />
      </VStack>
    );
  }

  return (
    <div
      onClick={() => {
        if (!isReadonly) {
          store.send({
            type: 'setFocusedUtteranceId',
            utteranceId: utterance.id,
          });
        }
      }}
      className={!isReadonly ? 'w-full cursor-text py-2' : ''}
    >
      {utterance.hasHumanUpdatedText ? (
        <P
          className={legacyTextStyles.body({
            className:
              'whitespace-pre-line rounded-full px-2 text-text-positive',
          })}
          data-speaker={utterance.speaker}
          data-edited-by={utterance.updatedById}
        >
          {utterance.text}
        </P>
      ) : (
        <UnfocusedUtteranceLine
          utterance={utterance}
          nextUtterance={nextUtterance}
          showConfidenceStyles={showConfidenceStyles}
          splitUtterance={splitUtterance}
          shouldDisplaySplitter={shouldDisplaySplitter}
          isReadonly={isReadonly}
        />
      )}
    </div>
  );
};

// A special version of utterance line used when in focus mode
const FocusedUtteranceLine: React.FC<{
  utterance: Utterance;
  nextUtterance?: Utterance | null;
  showConfidenceStyles: boolean;
  onWordClick: () => void;
  splitUtterance: (params: { utteranceId: string; fragmentId: string }) => void;
  shouldDisplaySplitter: (
    nextStartAt: number | null | undefined,
    endAt: number | null | undefined,
  ) => boolean;
}> = ({
  utterance,
  nextUtterance,
  showConfidenceStyles,
  onWordClick,
  splitUtterance,
  shouldDisplaySplitter,
}) => (
  <HStack className="whitespace-pre-wrap px-2 py-1" gap={0.75}>
    {utterance.fragments.map((f, fIdx) => {
      const hasNextFragment = Boolean(utterance.fragments[fIdx + 1]);
      const explictlyShowSplitter = shouldDisplaySplitter(
        nextUtterance?.startAt,
        f.endAt,
      );
      const showSplitter = hasNextFragment || explictlyShowSplitter;

      return (
        <HStack gap={0} key={f.id} className="group inline-flex" wFull={false}>
          <span
            className="inline-flex cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onWordClick();
            }}
          >
            <MemoizedTranscriptWord
              showConfidenceStyles={showConfidenceStyles}
              u={{
                id: utterance.id,
                start: utterance.startAt,
                end:
                  nextUtterance?.startAt && utterance.endAt
                    ? Math.max(nextUtterance.startAt - 0.3, utterance.endAt)
                    : utterance.endAt,
              }}
              w={{
                confidence: f.confidence,
                end: f.endAt,
                start: f.startAt,
                word: f.text,
                fragmentId: f.id,
                speaker: utterance.speaker,
              }}
            />
          </span>
          {showSplitter && (
            <MemoizedUtteranceSplitter
              utteranceId={utterance.id}
              fragmentId={f.id}
              tooltipLabel={
                hasNextFragment
                  ? 'Split to a new speaker'
                  : `${getReadableTime((nextUtterance?.startAt ?? 0) - (f.endAt ?? 0))} seconds of no transcript. Split new utterance`
              }
              onSplit={splitUtterance}
              explicitlyVisible={!hasNextFragment && explictlyShowSplitter}
            />
          )}
        </HStack>
      );
    })}
  </HStack>
);

const UnfocusedUtteranceLine: React.FC<RegularTranscriptProps> = ({
  utterance,
  nextUtterance,
  showConfidenceStyles,
  splitUtterance,
  shouldDisplaySplitter,
  isReadonly,
}) => (
  <React.Fragment>
    {utterance.fragments.map((f, fIdx) => {
      const hasNextFragment = Boolean(utterance.fragments[fIdx + 1]);
      const explictlyShowSplitter = shouldDisplaySplitter(
        nextUtterance?.startAt,
        f.endAt,
      );
      const showSplitter = hasNextFragment || explictlyShowSplitter;

      return (
        <HStack
          gap={1}
          key={f.id}
          className="group inline-flex first:pl-2"
          wFull={false}
        >
          <MemoizedTranscriptWord
            showConfidenceStyles={showConfidenceStyles}
            u={{
              id: utterance.id,
              start: utterance.startAt,
              end:
                //This allows us to capture parts between the utterances
                nextUtterance?.startAt && utterance.endAt
                  ? Math.max(nextUtterance.startAt - 0.3, utterance.endAt)
                  : utterance.endAt,
            }}
            w={{
              confidence: f.confidence,
              end: f.endAt,
              start: f.startAt,
              word: f.text,
              fragmentId: f.id,
              speaker: utterance.speaker,
            }}
          />
          {Boolean(!isReadonly) && showSplitter && (
            <MemoizedUtteranceSplitter
              utteranceId={utterance.id}
              fragmentId={f.id}
              tooltipLabel={
                hasNextFragment
                  ? 'Split to a new speaker'
                  : `${getReadableTime((nextUtterance?.startAt ?? 0) - (f.endAt ?? 0))} seconds of no transcript. Split new utterance`
              }
              onSplit={splitUtterance}
              explicitlyVisible={!hasNextFragment && explictlyShowSplitter}
            />
          )}
        </HStack>
      );
    })}
  </React.Fragment>
);
